import type {
	BoundActions,
	HookActionsFunction,
	HookFunction,
	HookStateFunction,
	SubscriberComponent,
} from 'react-sweet-state';
import {
	createStore,
	createHook,
	createSubscriber,
	createActionsHook,
	createStateHook,
} from 'react-sweet-state';

import { findUnresolvedCommentIndex } from './findUnresolvedCommentIndex.ts';

type InlineCommentsState = {
	unresolvedInlineComments: string[];
};

export type InlineCommentsContextType = {
	updateUnresolvedInlineComment: (unresolvedInlineComments: string[]) => void;
};

const initialState: InlineCommentsState = {
	unresolvedInlineComments: [],
};

const actions = {
	updateUnresolvedInlineComment:
		(unresolvedInlineComments: string[]) =>
		({ setState }: any): void => {
			setState({ unresolvedInlineComments });
		},
	addUnresolvedInlineComment:
		(unresolvedAnnotationId: string | null, pageModes: string) =>
		({ setState, getState }: any): void => {
			if (!unresolvedAnnotationId) {
				return;
			}

			const { unresolvedInlineComments } = getState();

			setState({
				unresolvedInlineComments: findUnresolvedCommentIndex(
					unresolvedAnnotationId,
					unresolvedInlineComments,
					pageModes,
				),
			});
		},
	removeUnresolvedInlineComment:
		(unresolvedAnnotationId: string | null) =>
		({ setState, getState }: any): void => {
			if (!unresolvedAnnotationId) {
				return;
			}

			const { unresolvedInlineComments } = getState();
			const updatedUnresolvedInlineComments = [...unresolvedInlineComments];

			const unresolvedAnnotationIdx =
				updatedUnresolvedInlineComments.indexOf(unresolvedAnnotationId);

			// Remove the passed in comment if it exists and reset the state
			if (unresolvedAnnotationIdx > -1) {
				updatedUnresolvedInlineComments.splice(unresolvedAnnotationIdx, 1);

				setState({
					unresolvedInlineComments: updatedUnresolvedInlineComments,
				});
			}
		},
};

const InlineCommentsStore = createStore({
	initialState,
	actions,
	name: 'InlineCommentsStore',
});

export const useInlineComments: HookFunction<
	InlineCommentsState,
	BoundActions<
		InlineCommentsState,
		{
			updateUnresolvedInlineComment: (
				unresolvedInlineComments: string[],
			) => ({ setState }: any) => void;
			addUnresolvedInlineComment: (
				unresolvedAnnotationId: string | null,
				pageModes: string,
			) => ({ setState, getState }: any) => void;
			removeUnresolvedInlineComment: (
				unresolvedAnnotationId: string | null,
			) => ({ setState, getState }: any) => void;
		}
	>,
	void
> = createHook(InlineCommentsStore);

export const useInlineCommentsActions: HookActionsFunction<
	BoundActions<
		InlineCommentsState,
		{
			updateUnresolvedInlineComment: (
				unresolvedInlineComments: string[],
			) => ({ setState }: any) => void;
			addUnresolvedInlineComment: (
				unresolvedAnnotationId: string | null,
				pageModes: string,
			) => ({ setState, getState }: any) => void;
			removeUnresolvedInlineComment: (
				unresolvedAnnotationId: string | null,
			) => ({ setState, getState }: any) => void;
		}
	>
> = createActionsHook(InlineCommentsStore);
export const useInlineCommentsState: HookStateFunction<InlineCommentsState, void> =
	createStateHook(InlineCommentsStore);

export const InlineCommentsSubscriber: SubscriberComponent<
	InlineCommentsState,
	BoundActions<
		InlineCommentsState,
		{
			updateUnresolvedInlineComment: (
				unresolvedInlineComments: string[],
			) => ({ setState }: any) => void;
			addUnresolvedInlineComment: (
				unresolvedAnnotationId: string | null,
				pageModes: string,
			) => ({ setState, getState }: any) => void;
			removeUnresolvedInlineComment: (
				unresolvedAnnotationId: string | null,
			) => ({ setState, getState }: any) => void;
		}
	>,
	unknown
> = createSubscriber(InlineCommentsStore);
